import {
  Autocomplete,
  Button,
  FormControl,
  FormControlLabel,
  FormLabel,
  Grid,
  MenuItem,
  Radio,
  RadioGroup,
  TextField,
  Typography,
} from '@mui/material';
import { Box, Stack } from '@mui/system';
import { DateTimePicker, renderTimeViewClock } from '@mui/x-date-pickers';
import { useFormik } from 'formik';
import moment from 'moment';
import { CSSProperties, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { clockInActivityType } from 'src/config';
import useCurrentUser from 'src/hooks/use-current-user';
import useFeatureToggle from 'src/hooks/use-feature-toggle';
import { LaborLocationTypeIcons } from 'src/pages/therapist-dashboard/dashboard-icon';
import { permissions } from 'src/paths';
import { getAllTherapistAction } from 'src/redux/actions/documentationAction';
import {
  addLaborHoursAction,
  editLaborHoursAction,
  getLocationTypeLaborHr,
} from 'src/redux/actions/laborHourActions';
import {
  convertDateToSelectedTimeZone,
  convertIntoIsoFormat,
  formatDateWithTimezone,
  formatDateWithTimezoneWithZeroSecond,
} from 'src/utils/convertDateToSelectedTimeZone';
import * as Yup from 'yup';

const {
  READ_FLOAT_CODE,
  READ_HOL_CODE,
  READ_PTO_CODE,
  READ_BEREV_CODE,
  READ_PRNEDU_CODE,
  READ_REHAV_TRAV_CODE,
  READ_JURY_CODE,
} = permissions;

type Props = {
  close: any;
  openHoursType: any;
  selectedDate: any;
};

const EditLaborManagement = (props: Props) => {
  const { close, openHoursType, selectedDate } = props;
  const getFacilityTimeZone = localStorage.getItem('facility_timezone') || '';
  const [checkActivityType] = useFeatureToggle(clockInActivityType, 'ENTERPRISE');
  const permissionData = useSelector(
    (state: any) => state?.getTherapistProfileReducer?.response?.data?.currentPermissions
  );
  const dispatch = useDispatch();
  const modalStyle: CSSProperties = {
    position: 'fixed',
    left: '50%',
    top: '50%',
    transform: 'translate(-50%, -50%)',
    WebkitTransform: 'translate(-50%, -50%)',
  };
  const modalContainerStyle: CSSProperties = {
    backgroundColor: '#fff',
    width: '600px',
    height: 'auto',
    paddingBottom: '24px',
  };

  // const [selectedHours, setSelectedHours] = useState({
  //   startTime: moment(new Date().setHours(9, 0, 0, 0)).format('HH:mm:ss'),
  //   endTime: moment(new Date().setHours(17, 0, 0, 0)).format('HH:mm:ss'),
  // });
  const allTherapist = useSelector((state: any) => state.getAllTherapistReducer);
  const getTherapistProfile = useSelector(
    (state: any) => state.getTherapistProfileReducer?.response?.data
  );
  const locationType = useSelector((state: any) => state.getLocationTypeLaborHrReducer);
  const addLaborHrsResponse = useSelector((state: any) => state.addLaborHoursReducer);
  const editLaborHrsResponse = useSelector((state: any) => state.editLaborHoursReducer);
  const getLaborCodesResponse = useSelector((state: any) => state.getLaborCodesReducer?.response);
  const getLaborCodesResponseWithPermission = getLaborCodesResponse?.filter((code: any) => {
    if (code?.key === 'REHAB_TRAV') {
      if (permissionData?.includes(READ_REHAV_TRAV_CODE)) return true;
      else return false;
    } else if (code?.key === 'FLOAT') {
      if (permissionData?.includes(READ_FLOAT_CODE)) return true;
      else return false;
    } else if (code?.key === 'PRNEDU') {
      if (permissionData?.includes(READ_PRNEDU_CODE)) return true;
      else return false;
    } else if (code?.key === 'BEREV') {
      if (permissionData?.includes(READ_BEREV_CODE)) return true;
      else return false;
    } else if (code?.key === 'HOL') {
      if (permissionData?.includes(READ_HOL_CODE)) return true;
      else return false;
    } else if (code?.key === 'PTO') {
      if (permissionData?.includes(READ_PTO_CODE)) return true;
      else return false;
    } else if (code?.key === 'JURY') {
      if (permissionData?.includes(READ_JURY_CODE)) return true;
      else return false;
    }
    return false;
  });

  const isLaborCodeDropdownShow =
    permissionData?.includes(READ_JURY_CODE) ||
    permissionData?.includes(READ_PTO_CODE) ||
    permissionData?.includes(READ_HOL_CODE) ||
    permissionData?.includes(READ_BEREV_CODE) ||
    permissionData?.includes(READ_PRNEDU_CODE) ||
    permissionData?.includes(READ_REHAV_TRAV_CODE) ||
    permissionData?.includes(READ_FLOAT_CODE);
  useEffect(() => {
    dispatch(getAllTherapistAction(null, null, null));
    dispatch(getLocationTypeLaborHr());
  }, []);

  interface LaborHours {
    type: string;
    therapist?: any;
    mileage?: any;
    clockIn?: Date;
    clockOut?: Date;
  }

  function extractTimeFromDate(date: any) {
    const d = new Date(date);
    return d.getHours() * 3600 + d.getMinutes() * 60 + d.getSeconds();
  }

  const laborHoursValidation: Yup.ObjectSchema<LaborHours> = Yup.object().shape({
    type: Yup.string().required('Please select a location'),
    therapist: Yup.string().required('Please select a therapist'),
    laborCode: Yup.object()
      .shape({
        displayName: Yup.string().nullable(),
        key: Yup.string().nullable(),
      })
      .nullable(),
    clockInActivityType: Yup.string().nullable(),
    mileage: Yup.number().test(
      'custom-validation',
      'Please Enter valid Mileage',
      // @ts-ignore
      function (value: any) {
        const type = this.parent.type;
        if (type === 'MILEAGE') {
          return Yup.number().min(0).validateSync(value);
        }
        return true;
      }
    ),
    clockIn: Yup.date()
      .test(
        'custom-validation',
        'Please Enter valid time',
        // @ts-ignore
        function (value: any) {
          const type = this.parent.type;
          if (type !== 'MILEAGE') {
            return Yup.date().min(0).validateSync(value);
          }
          return true;
        }
      )
      .test(
        'clock-out-after-clock-in',
        'Clock Out time must not be earlier or equal than clock In time',
        function (value) {
          const type = this.parent.type;
          if (type !== 'MILEAGE') {
            const clockOutValue = this.parent.clockOut;
            if (value && clockOutValue) {
              const clockInTime = extractTimeFromDate(value);
              const clockOutTime = extractTimeFromDate(clockOutValue);
              return clockInTime <= clockOutTime;
            }
          }
          return true;
        }
      )
      .test('no-future-date-or-time', 'Cannot select a future date or time', function (value) {
        const type = this.parent.type;
        if (type !== 'MILEAGE') {
          const currentDate = new Date();

          if (!value) return false;
          const selectedDate =
            openHoursType?.type === 'EDIT'
              ? new Date(convertDateToSelectedTimeZone(value))
              : new Date(value);
          return selectedDate <= new Date(convertDateToSelectedTimeZone(currentDate));
        }
        return true;
      }),
    clockOut: Yup.date()
      .test(
        'custom-validation',
        'Please Enter valid time',
        // @ts-ignore
        function (value: any) {
          const type = this.parent.type;
          if (type !== 'MILEAGE') {
            return Yup.date().min(0).validateSync(value);
          }
          return true;
        }
      )
      .test(
        'clock-out-after-clock-in',
        'Clock Out time must not be earlier or equal than clock In time',
        function (value) {
          const type = this.parent.type;
          if (type !== 'MILEAGE') {
            const clockInValue = this.parent.clockIn;
            if (value && clockInValue) {
              const clockInTime = extractTimeFromDate(clockInValue);
              const clockOutTime = extractTimeFromDate(value);
              return clockOutTime > clockInTime;
            }
          }
          return true;
        }
      )
      .test('no-future-date-or-time', 'Cannot select a future date or time', function (value) {
        const type = this.parent.type;
        if (type !== 'MILEAGE') {
          const currentDate = new Date();
          if (!value) return false;
          const selectedDate = new Date(value);
          return selectedDate <= new Date(convertDateToSelectedTimeZone(currentDate));
        }
        return true;
      }),
  });

  const getDateInLocalIsoFormat = (date: any) => {
    const dateNewDate = new Date(date);
    const timeValue = moment(dateNewDate)?.format('HH:mm:ss');
    return `${moment(dateNewDate).format('YYYY-MM-DD')}T${timeValue}.000Z`;
  };

  const getDateInLocalIsoFormatWithoutTime = (date: any) => {
    const dateNewDate = new Date(date);
    return `${moment(dateNewDate).format('YYYY-MM-DD')}T00:00:00.000Z`;
  };

  const currentRole = useSelector(
    (state: any) => state?.getTherapistProfileReducer?.response?.data?.currentRoles
  );
  const [providerId] = useCurrentUser(['providerId']);

  const formik = useFormik({
    initialValues: {
      type: openHoursType?.type === 'EDIT' ? openHoursType?.value?.locationType : '',
      laborCode:
        openHoursType?.type === 'EDIT'
          ? {
              key: openHoursType?.value?.laborCode,
              displayName:
                getLaborCodesResponse?.find(
                  (code: any) => code?.key === openHoursType?.value?.laborCode
                )?.displayName || '',
            }
          : {
              displayName: '',
              key: '',
            },
      therapist: openHoursType?.type === 'EDIT' ? openHoursType?.value?.therapistId : '',
      mileage:
        openHoursType?.type === 'EDIT' && !!openHoursType?.value?.mileage
          ? openHoursType?.value?.mileage
          : '',
      clockIn:
        openHoursType?.type === 'EDIT' && !!openHoursType?.value?.clockIn
          ? new Date(convertDateToSelectedTimeZone(openHoursType?.value?.clockIn))
          : selectedDate,
      clockOut:
        openHoursType?.type === 'EDIT' && !!openHoursType?.value?.clockIn
          ? new Date(convertDateToSelectedTimeZone(openHoursType?.value?.clockOut))
          : selectedDate,
      clockInActivityType:
        openHoursType?.type === 'EDIT' ? openHoursType?.value?.clockInActivityType : '',
    },
    validationSchema: laborHoursValidation,
    onSubmit: (values) => {
      if (openHoursType?.type === 'EDIT') {
        if (values?.type === 'MILEAGE') {
          dispatch(
            editLaborHoursAction({
              locationType: values?.type,
              therapistId: values?.therapist,
              laborCode: null,
              mileage: values?.mileage,
              labourManagementId: openHoursType?.value?.labourManagementId,
              selectDate: convertIntoIsoFormat(formatDateWithTimezone(selectedDate)),
              clockInActivityType: null,
              param: {
                zoneId: getFacilityTimeZone,
              },
            })
          );
        } else {
          dispatch(
            editLaborHoursAction({
              locationType: values?.type,
              therapistId: values?.therapist,
              laborCode: values?.type === 'BUILDING' ? values?.laborCode?.key : null,
              labourManagementId: openHoursType?.value?.labourManagementId,
              clockIn: convertIntoIsoFormat(formatDateWithTimezoneWithZeroSecond(values?.clockIn)),
              clockOut: convertIntoIsoFormat(
                formatDateWithTimezoneWithZeroSecond(values?.clockOut)
              ),
              clockInActivityType:
                values?.clockInActivityType && values?.type === 'BUILDING'
                  ? values?.clockInActivityType
                  : null,
              selectDate: convertIntoIsoFormat(formatDateWithTimezone(selectedDate)),
              param: {
                zoneId: getFacilityTimeZone,
              },
            })
          );
        }
        return;
      } else {
        if (values?.type === 'MILEAGE') {
          dispatch(
            addLaborHoursAction({
              locationType: values?.type,
              laborCode: values?.type === 'BUILDING' ? values?.laborCode?.key : null,
              therapistId: values?.therapist,
              mileage: values?.mileage,
              selectDate: convertIntoIsoFormat(formatDateWithTimezone(selectedDate)),
              clockInActivityType: null,
              param: {
                zoneId: getFacilityTimeZone,
              },
            })
          );
        } else {
          dispatch(
            addLaborHoursAction({
              locationType: values?.type,
              laborCode: values?.type === 'BUILDING' ? values?.laborCode?.key : null,
              therapistId: values?.therapist,
              clockIn: convertIntoIsoFormat(formatDateWithTimezoneWithZeroSecond(values?.clockIn)),
              clockOut: convertIntoIsoFormat(
                formatDateWithTimezoneWithZeroSecond(values?.clockOut)
              ),
              clockInActivityType:
                values?.clockInActivityType && values?.type === 'BUILDING'
                  ? values?.clockInActivityType
                  : null,
              selectDate: convertIntoIsoFormat(formatDateWithTimezone(selectedDate)),
              param: {
                zoneId: getFacilityTimeZone,
              },
            })
          );
        }
      }
    },
  });

  const filteredOptions = allTherapist?.response?.data
    ?.slice()
    .sort((a: any, b: any) => a?.lastName?.localeCompare(b?.lastName));

  function calculateTimeDifference(clockIn: any, clockOut: any): string {
    const timeDifferenceInMillis: number =
      new Date(clockOut).getTime() - new Date(clockIn).getTime();
    const totalSeconds = Math.floor(timeDifferenceInMillis / 1000);
    const sign = totalSeconds < 0 ? '-' : '';
    const absTotalSeconds = Math.abs(totalSeconds);

    const hours = Math.floor(absTotalSeconds / 3600);
    const remainingSecondsAfterHours = absTotalSeconds % 3600;
    const minutes = Math.floor(remainingSecondsAfterHours / 60);
    const seconds = remainingSecondsAfterHours % 60;

    const hoursString = String(!!hours ? hours : '0').padStart(2, '0');
    const minutesString = String(!!minutes ? minutes : '0').padStart(2, '0');
    const secondsString = String(!!seconds ? seconds : '0').padStart(2, '0');

    return `${sign}${hoursString}:${minutesString}:${secondsString}`;
  }

  return (
    <form onSubmit={formik.handleSubmit}>
      <Box style={modalStyle}>
        <Box style={modalContainerStyle}>
          <Stack>
            <Box sx={{ padding: '24px 24px 24px 24px' }}>
              <Typography variant="h6">
                {openHoursType?.type === 'EDIT' ? 'Update Labor Hours' : 'Add New Labor Hours'}{' '}
              </Typography>
            </Box>
            <Stack
              spacing={3}
              sx={{ padding: '24px 24px 24px 24px' }}
            >
              <Autocomplete
                disablePortal
                onBlur={formik.handleBlur}
                fullWidth
                getOptionLabel={(option: any) => {
                  return option?.lastName && option?.firstName
                    ? `${option.lastName} ${option.firstName}`
                    : '';
                }}
                onChange={(event, newValue: any) => {
                  formik.setFieldValue('therapist', newValue?.uuid);
                }}
                value={
                  filteredOptions?.find((option: any) => option.uuid === formik.values.therapist) ||
                  null
                }
                id="combo-box-demo"
                isOptionEqualToValue={(option: any, value: any) => {
                  return option?.uuid === value?.uuid;
                }}
                renderOption={(props, option) => {
                  return (
                    <MenuItem
                      key={option?.uuid}
                      value={option?.uuid}
                      {...props}
                    >
                      {`${option?.lastName} ${option?.firstName}`}
                    </MenuItem>
                  );
                }}
                options={filteredOptions || []}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Therapist Name"
                    error={!!(formik.touched.therapist && formik.errors.therapist)}
                    helperText={formik.touched.therapist && (formik.errors.therapist as any)}
                  />
                )}
                loading={allTherapist?.loading}
              />
              <Grid
                xs={12}
                display={'flex'}
                gap={2}
                justifyContent={'space-between'}
              >
                <TextField
                  error={!!(formik.touched.type && formik.errors.type)}
                  fullWidth
                  helperText={formik.touched.type && (formik.errors.type as any)}
                  label="Location Type"
                  name="type"
                  onBlur={formik.handleBlur}
                  onChange={formik.handleChange}
                  value={formik.values.type}
                  select
                >
                  {locationType?.loading && (
                    <MenuItem
                      sx={{
                        cursor: 'none',
                        pointerEvents: 'none',
                      }}
                    >
                      location Type loading...
                    </MenuItem>
                  )}
                  {!!locationType?.response?.length &&
                    !locationType?.loading &&
                    locationType?.response?.map((option: any) => (
                      <MenuItem
                        key={option?.key}
                        value={option?.key}
                        sx={{ gap: 1, display: 'flex', alignItems: 'center' }}
                      >
                        <LaborLocationTypeIcons
                          style={{
                            marginRight: '10px',
                            position: 'relative',
                            top: '4px',
                          }}
                          iconType={option?.key}
                        />
                        {option?.displayName}
                      </MenuItem>
                    ))}
                </TextField>
                {formik.values?.type === 'BUILDING' && isLaborCodeDropdownShow && (
                  <Autocomplete
                    disablePortal
                    fullWidth
                    onBlur={formik.handleBlur}
                    getOptionLabel={(option: any) => option?.displayName || ''}
                    onChange={(event, newValue: any) => {
                      formik.setFieldValue('laborCode', newValue);
                    }}
                    value={formik.values.laborCode}
                    id="combo-box-demo"
                    renderOption={(props, option) => {
                      return (
                        <MenuItem
                          key={option?.key}
                          value={option?.key}
                          {...props}
                        >
                          {option?.displayName}
                        </MenuItem>
                      );
                    }}
                    options={getLaborCodesResponseWithPermission || []}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Labor Code"
                      />
                    )}
                  />
                )}
              </Grid>

              {formik.values.type === 'BUILDING' || formik.values.type === 'DRIVE' ? (
                <>
                  <DateTimePicker
                    viewRenderers={{
                      hours: renderTimeViewClock,
                      minutes: renderTimeViewClock,
                      seconds: renderTimeViewClock,
                    }}
                    onChange={(e) => {
                      formik.setFieldValue('clockIn', e);
                    }}
                    label="Clock In"
                    value={formik.values.clockIn}
                    slotProps={{
                      textField: {
                        name: 'clockIn',
                        error: !!formik.errors.clockIn,
                        // @ts-ignore
                        helperText: formik.errors.clockIn,
                      },
                    }}
                    // disableFuture
                    views={['hours', 'minutes']}
                  />

                  <DateTimePicker
                    label="Clock Out"
                    onChange={(e) => {
                      formik.setFieldValue('clockOut', e);
                    }}
                    value={formik.values.clockOut}
                    // disableFuture
                    views={['hours', 'minutes']}
                    slotProps={{
                      textField: {
                        name: 'clockOut',
                        error: !!formik.errors.clockOut,
                        // @ts-ignore
                        helperText: formik.errors.clockOut,
                      },
                    }}
                    viewRenderers={{
                      hours: renderTimeViewClock,
                      minutes: renderTimeViewClock,
                      seconds: renderTimeViewClock,
                    }}
                  />

                  <TextField
                    label="Total Hours"
                    value={
                      !!calculateTimeDifference(formik.values.clockIn, formik.values.clockOut)
                        ? calculateTimeDifference(formik.values.clockIn, formik.values.clockOut)
                        : 0
                    }
                    fullWidth
                    disabled
                  />
                </>
              ) : null}

              {formik.values.type === 'MILEAGE' ? (
                <TextField
                  label="Mileage"
                  name="mileage"
                  error={!!(formik.touched.mileage && formik.errors.mileage)}
                  fullWidth
                  helperText={formik.touched.mileage && (formik.errors.mileage as any)}
                  onBlur={formik.handleBlur}
                  onChange={formik.handleChange}
                  value={formik.values.mileage}
                />
              ) : null}
              {getTherapistProfile?.currentRoles?.includes('dor') && checkActivityType ? (
                <>
                  {formik.values?.type === 'BUILDING' ? (
                    <FormControl sx={{ paddingLeft: '10px' }}>
                      <FormLabel id="demo-row-radio-buttons-group-label">Activity Type</FormLabel>
                      <RadioGroup
                        row
                        aria-labelledby="demo-row-radio-buttons-group-label"
                        onChange={formik.handleChange}
                        value={formik.values.clockInActivityType}
                        name="clockInActivityType"
                      >
                        <FormControlLabel
                          value="THERAPY"
                          control={<Radio />}
                          label="Therapy"
                        />
                        <FormControlLabel
                          value="ADMINISTRATIVE"
                          control={<Radio />}
                          label="Administrative"
                        />
                      </RadioGroup>
                    </FormControl>
                  ) : null}
                </>
              ) : null}
            </Stack>
          </Stack>
          <Box
            sx={{
              alignItems: 'center',
              display: 'flex',
              marginRight: '24px',
            }}
          >
            <Box sx={{ flexGrow: 1 }} />
            <Button
              color="inherit"
              onClick={close}
            >
              Cancel
            </Button>
            <Button
              sx={{ ml: 1 }}
              type="submit"
              variant="contained"
              disabled={addLaborHrsResponse?.loading || editLaborHrsResponse?.loading}
            >
              Save
            </Button>
          </Box>
        </Box>
      </Box>
    </form>
  );
};

export default EditLaborManagement;
